/**
 * Hooks TanStack Query per le SCOMMESSE (Andrea 2026-06-11/13).
 *
 * "Partite di cartello": l'admin seleziona, per una giornata, alcune partite
 * scommettibili. Due modalità:
 *   - P2P: tra i 2 presidenti della partita (uno propone, l'altro accetta).
 *   - Banco esterno: gli altri presidenti puntano su 1/X/2 con quote admin.
 *
 * Crediti = budget squadra (fantamilioni). Le scommesse chiudono all'inizio
 * della giornata (stessa deadline della formazione: 15 min prima della prima
 * partita SA) → campo `deadline` + flag `chiusa` su [[CartelloItem]].
 *
 * Endpoint:
 *   GET    /scommesse/cartello                       → lista partite di cartello
 *   GET    /scommesse/cartello/{id}/scommesse        → p2p + banco di un cartello
 *   GET    /scommesse/resoconto?stagione_id=         → chi ha scommesso cosa, per giornata
 *   POST   /scommesse/p2p                            → proponi sfida P2P
 *   POST   /scommesse/p2p/{id}/rispondi              → accetta/rifiuta sfida
 *   POST   /scommesse/banco                          → piazza scommessa col banco
 *   POST   /scommesse/admin/cartello                 → (admin) crea partita di cartello
 *   PATCH  /scommesse/admin/cartello/{id}            → (admin) quote/banco/stato
 *   DELETE /scommesse/admin/cartello/{id}            → (admin) elimina (se libera)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export type StatoCartello = "aperta" | "chiusa" | "risolta";
export type EsitoPartita = "1" | "x" | "2";
export type StatoScommessaP2P = "proposta" | "accettata" | "rifiutata" | "annullata" | "risolta";
export type StatoScommessaBanco = "piazzata" | "vinta" | "persa" | "annullata";

export interface CartelloItem {
  id: string;
  stagione_id: string;
  giornata_id: string;
  partita_id: string;
  squadra_casa_id: string;
  squadra_casa_nome: string;
  squadra_trasferta_id: string;
  squadra_trasferta_nome: string;
  stato: StatoCartello;
  banco_attivo: boolean;
  quota_1: string | null;
  quota_x: string | null;
  quota_2: string | null;
  esito: EsitoPartita | null;
  /** Chiusura scommesse = inizio giornata. null se orario ignoto. */
  deadline: string | null;
  /** true se non si può più scommettere (stato != aperta o deadline passata). */
  chiusa: boolean;
  /** Andrea 2026-10-30: di quale competizione e' la partita. */
  competizione_nome?: string | null;
  competizione_tipo?: string | null;
}

export interface ScommessaP2PItem {
  id: string;
  partita_cartello_id: string;
  proponente_squadra_id: string;
  accettante_squadra_id: string;
  importo_milioni: string;
  stato: StatoScommessaP2P;
  esito: string | null;
}

export interface ScommessaBancoItem {
  id: string;
  partita_cartello_id: string;
  squadra_id: string;
  pronostico: EsitoPartita;
  importo_milioni: string;
  quota_applicata: string;
  stato: StatoScommessaBanco;
}

export interface ScommesseCartello {
  p2p: ScommessaP2PItem[];
  banco: ScommessaBancoItem[];
}

// ============================================================
// Queries
// ============================================================
export function useCartello(opts?: {
  stagioneId?: string | null;
  giornataId?: string | null;
  soloAperte?: boolean;
}) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  const stagioneId = opts?.stagioneId ?? null;
  const giornataId = opts?.giornataId ?? null;
  const soloAperte = opts?.soloAperte ?? false;
  return useQuery({
    queryKey: ["scommesse", "cartello", stagioneId, giornataId, soloAperte],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (stagioneId) p.set("stagione_id", stagioneId);
      if (giornataId) p.set("giornata_id", giornataId);
      if (soloAperte) p.set("solo_aperte", "true");
      const qs = p.toString();
      const res = await api.get<CartelloItem[]>(`/scommesse/cartello${qs ? `?${qs}` : ""}`);
      return res.data;
    },
    enabled: isAuth,
  });
}

export function useScommesseCartello(cartelloId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["scommesse", "cartello", cartelloId, "scommesse"],
    queryFn: async () => {
      const res = await api.get<ScommesseCartello>(`/scommesse/cartello/${cartelloId}/scommesse`);
      return res.data;
    },
    enabled: isAuth && !!cartelloId,
  });
}

// ============================================================
// Resoconto pubblico (Andrea 2026-09-17): chi ha scommesso cosa, giornata
// per giornata, con vincite possibili, esiti e bilanci. Lo vedono tutti.
// ============================================================
export interface ResocontoBancoItem {
  id: string;
  squadra_id: string;
  pronostico: EsitoPartita;
  importo_milioni: string;
  quota_applicata: string;
  vincita_potenziale: string;
  stato: StatoScommessaBanco;
  /** null finche' la puntata e' aperta; poi +guadagno o -puntata. */
  netto: string | null;
}

export interface ResocontoP2PItem {
  id: string;
  proponente_squadra_id: string;
  accettante_squadra_id: string;
  importo_milioni: string;
  /** Quel che prende chi vince: due volte la puntata. */
  piatto: string;
  stato: StatoScommessaP2P;
  esito: "vinta_proponente" | "vinta_accettante" | "pareggio_rimborso" | null;
  vincitore_squadra_id: string | null;
}

export interface ResocontoPartitaItem {
  cartello_id: string;
  partita_id: string;
  competizione_nome: string;
  competizione_tipo?: string | null;
  squadra_casa_id: string;
  squadra_trasferta_id: string;
  stato: StatoCartello;
  banco_attivo: boolean;
  quota_1: string | null;
  quota_x: string | null;
  quota_2: string | null;
  esito: EsitoPartita | null;
  gol_casa: number | null;
  gol_trasferta: number | null;
  p2p: ResocontoP2PItem[];
  banco: ResocontoBancoItem[];
}

/** Bilancio scommesse di una squadra. puntato/incassato/netto contano solo
 * le scommesse chiuse; `in_gioco` quelle ancora aperte. */
export interface BilancioScommesseItem {
  squadra_id: string;
  giocate: number;
  vinte: number;
  perse: number;
  puntato: string;
  incassato: string;
  netto: string;
  in_gioco: string;
}

export interface ResocontoGiornataItem {
  giornata_id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
  deadline: string | null;
  chiusa: boolean;
  partite: ResocontoPartitaItem[];
  bilancio: BilancioScommesseItem[];
}

export interface ResocontoScommesse {
  stagione_id: string;
  giornate: ResocontoGiornataItem[];
  bilancio: BilancioScommesseItem[];
  nomi_squadre: Record<string, string>;
}

export function useResocontoScommesse(stagioneId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["scommesse", "resoconto", stagioneId],
    queryFn: async () => {
      const res = await api.get<ResocontoScommesse>(
        `/scommesse/resoconto?stagione_id=${stagioneId}`,
      );
      return res.data;
    },
    enabled: isAuth && !!stagioneId,
  });
}

// ============================================================
// Mutations — utente
// ============================================================
function useInvalidaScommesse() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["scommesse"] });
    // I movimenti budget cambiano il saldo: rinfresca anche budget.
    qc.invalidateQueries({ queryKey: ["budget"] });
  };
}

export function useProponiP2P() {
  const invalida = useInvalidaScommesse();
  return useMutation({
    mutationFn: async (args: {
      cartelloId: string;
      proponenteSquadraId: string;
      importoMilioni: number;
    }) => {
      const res = await api.post<ScommessaP2PItem>("/scommesse/p2p", {
        cartello_id: args.cartelloId,
        proponente_squadra_id: args.proponenteSquadraId,
        importo_milioni: args.importoMilioni,
      });
      return res.data;
    },
    onSuccess: invalida,
  });
}

export function useRispondiP2P() {
  const invalida = useInvalidaScommesse();
  return useMutation({
    mutationFn: async (args: { scommessaId: string; accetta: boolean }) => {
      const res = await api.post<ScommessaP2PItem>(`/scommesse/p2p/${args.scommessaId}/rispondi`, {
        accetta: args.accetta,
      });
      return res.data;
    },
    onSuccess: invalida,
  });
}

export function usePiazzaBanco() {
  const invalida = useInvalidaScommesse();
  return useMutation({
    mutationFn: async (args: {
      cartelloId: string;
      squadraId: string;
      pronostico: EsitoPartita;
      importoMilioni: number;
    }) => {
      const res = await api.post<ScommessaBancoItem>("/scommesse/banco", {
        cartello_id: args.cartelloId,
        squadra_id: args.squadraId,
        pronostico: args.pronostico,
        importo_milioni: args.importoMilioni,
      });
      return res.data;
    },
    onSuccess: invalida,
  });
}

// ============================================================
// Mutations — admin
// ============================================================
export function useCreaCartello() {
  const invalida = useInvalidaScommesse();
  return useMutation({
    mutationFn: async (args: {
      partitaId: string;
      bancoAttivo: boolean;
      quota1?: number | null;
      quotaX?: number | null;
      quota2?: number | null;
    }) => {
      const res = await api.post<CartelloItem>("/scommesse/admin/cartello", {
        partita_id: args.partitaId,
        banco_attivo: args.bancoAttivo,
        quota_1: args.quota1 ?? null,
        quota_x: args.quotaX ?? null,
        quota_2: args.quota2 ?? null,
      });
      return res.data;
    },
    onSuccess: invalida,
  });
}

export function useAggiornaCartello() {
  const invalida = useInvalidaScommesse();
  return useMutation({
    mutationFn: async (args: {
      cartelloId: string;
      bancoAttivo?: boolean | null;
      quota1?: number | null;
      quotaX?: number | null;
      quota2?: number | null;
      stato?: StatoCartello | null;
    }) => {
      const res = await api.patch<CartelloItem>(`/scommesse/admin/cartello/${args.cartelloId}`, {
        banco_attivo: args.bancoAttivo ?? null,
        quota_1: args.quota1 ?? null,
        quota_x: args.quotaX ?? null,
        quota_2: args.quota2 ?? null,
        stato: args.stato ?? null,
      });
      return res.data;
    },
    onSuccess: invalida,
  });
}

export function useEliminaCartello() {
  const invalida = useInvalidaScommesse();
  return useMutation({
    mutationFn: async (cartelloId: string) => {
      await api.delete(`/scommesse/admin/cartello/${cartelloId}`);
    },
    onSuccess: invalida,
  });
}

// ============================================================
// Quote proposte "da bookmaker" (Andrea 2026-09-11)
// ============================================================
export interface StimaSquadraQuote {
  squadra_id: string;
  nome: string;
  totale_atteso: number;
  totale_rosa: number;
  sigma: number;
  da_formazione: boolean;
  forma: number[];
  assenti: string[];
}

export interface QuotaProposta {
  partita_id: string;
  competizione_id: string;
  competizione_nome: string;
  fase: string;
  cartello_id: string | null;
  casa: StimaSquadraQuote;
  trasferta: StimaSquadraQuote;
  p1: number;
  px: number;
  p2: number;
  quota_1: string;
  quota_x: string;
  quota_2: string;
}

export interface QuoteProposteResponse {
  giornata_id: string;
  numero: number;
  numero_serie_a: number;
  n_voti: number;
  n_probabili: number;
  margine: number;
  proposte: QuotaProposta[];
}

/** Calcola le quote proposte per una giornata (solo lettura: non crea
 * ne' modifica cartelli). E' una mutation perche' parte a comando. */
export function useGeneraQuote() {
  return useMutation({
    mutationFn: async (args: { giornataId: string; margine?: number }) => {
      const res = await api.get<QuoteProposteResponse>("/scommesse/admin/quote-proposte", {
        params: { giornata_id: args.giornataId, margine: args.margine ?? 0.08 },
      });
      return res.data;
    },
  });
}
